import React from "react";
import DraggableStation from "../DraggableStation";
import DraggableSymbol from "../DraggableSymbol";
import CanvasRail from "../../../LiveDashboardModal/components/CanvasRail";
import LayerControls from "../../../LiveDashboardModal/components/LayerControls";
import { STATION_HALF_SIZE } from "../../utils/constants";
import { SYMBOL_HALF_SIZE, getSymbolById } from "../../utils/symbolTypes";
import { canvasMode, editorCursor } from "../../utils/editorCursor";

const LineCanvas = ({
  dimensions,
  canvasSize,
  placedStations,
  stationsVisible = true,
  selectedStationId,
  setSelectedStationId,
  handleLineDragStop,
  activeLayoutType,
  placedSymbols,
  symbolsVisible,
  handleSymbolDragStop,
  canvasRef,
  onCanvasClick,
  onDeleteSymbol,
  tripwireLayer = null,
  tripwiresVisible = true,
  // 圖層's 顯示 rows (editorLayerToggles) for this layout.
  layerToggles,
}) => {
  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div
        ref={canvasRef}
        style={{
          position: "absolute",
          left: 30,
          top: 60,
          width: `${canvasSize.width}px`,
          height: `${canvasSize.height}px`,
          // No 瀏覽 here: always editable, a tool crosshairs.
          cursor: editorCursor(canvasMode(true, !!onCanvasClick), "canvas"),
        }}
        onClick={onCanvasClick}
      >
        <div
          style={{
            position: "absolute",
            top: canvasSize.height / 2,
            left: 0,
            width: "100%",
            height: 2,
            background: "linear-gradient(to right, #595959, #8c8c8c)",
            borderRadius: 2,
            boxShadow: "0 0 6px rgba(89, 89, 89, 0.35)",
          }}
        />
        {/* Tick marks on the line */}
        <div
          style={{
            position: "absolute",
            top: canvasSize.height / 2 - 6,
            left: 0,
            width: 1,
            height: 12,
            backgroundColor: "#595959",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: canvasSize.height / 2 - 6,
            left: canvasSize.width / 2,
            width: 1,
            height: 12,
            backgroundColor: "#595959",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: canvasSize.height / 2 - 6,
            left: canvasSize.width - 1,
            width: 1,
            height: 12,
            backgroundColor: "#595959",
          }}
        />
        {/* Tick text labels below the line */}
        <div
          style={{
            position: "absolute",
            top: canvasSize.height / 2 + 8,
            left: 0,
            width: `${canvasSize.width}px`,
            fontSize: "12px",
            color: "#666",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 0,
              transform: "translateX(-50%)",
            }}
          >
            0m
          </span>
          <span
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            {(dimensions.width / 2).toFixed(1)}m
          </span>
          <span
            style={{
              position: "absolute",
              right: 0,
              transform: "translateX(50%)",
            }}
          >
            {dimensions.width}m
          </span>
        </div>
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {symbolsVisible &&
            placedSymbols &&
            placedSymbols
              .filter((symbol) => {
                const symbolType = getSymbolById(symbol.symbol_type);
                return symbolType?.compatibleLayouts?.includes("line");
              })
              .map((symbol) => {
                const hasPositionMeters = !!symbol.position_meters;
                const safeWidth = dimensions.width || 1;
                const baseXMeters = hasPositionMeters
                  ? symbol.position_meters.x
                  : safeWidth / 2;
                const clampedXMeters = Math.max(
                  0,
                  Math.min(safeWidth, baseXMeters),
                );
                const xPixels =
                  (clampedXMeters / (safeWidth || 1)) *
                    (canvasSize.width || 1) -
                  SYMBOL_HALF_SIZE;
                const lineCenterY =
                  (canvasSize.height || 0) / 2 - SYMBOL_HALF_SIZE;

                return (
                  <DraggableSymbol
                    key={symbol.id || `line-symbol-${symbol.symbol_type}`}
                    symbol={symbol}
                    layoutType="line"
                    activeLayoutType={activeLayoutType}
                    position={{
                      x: xPixels,
                      y: lineCenterY,
                    }}
                    bounds={{
                      left: -SYMBOL_HALF_SIZE,
                      right: (canvasSize.width || 0) - SYMBOL_HALF_SIZE,
                    }}
                    onDragStop={handleSymbolDragStop}
                    onDelete={onDeleteSymbol}
                    axis="x"
                  />
                );
              })}
          {stationsVisible &&
            placedStations.map((station) => {
              const hasPositionMeters = !!station.position_meters;
              const safeWidth = dimensions.width || 1;
              const baseXMeters = hasPositionMeters
                ? station.position_meters.x
                : safeWidth / 2;
              const clampedXMeters = Math.max(
                0,
                Math.min(safeWidth, baseXMeters),
              );
              const xPixels =
                (clampedXMeters / (safeWidth || 1)) * (canvasSize.width || 1) -
                STATION_HALF_SIZE;
              const lineCenterY =
                (canvasSize.height || 0) / 2 - STATION_HALF_SIZE;

              return (
                <DraggableStation
                  key={
                    station.id || `line-station-${station.device_id || station.name}`
                  }
                  station={station}
                  layoutType="line"
                  activeLayoutType={activeLayoutType}
                  position={{
                    x: xPixels,
                    y: lineCenterY,
                  }}
                  bounds={{
                    left: -STATION_HALF_SIZE,
                    right: (canvasSize.width || 0) - STATION_HALF_SIZE,
                  }}
                  selectedStationId={selectedStationId}
                  setSelectedStationId={setSelectedStationId}
                  onDragStop={handleLineDragStop}
                  axis="x"
                  scale={(canvasSize.width || 0) / (dimensions.width || 1)}
                />
              );
            })}
        </div>
        {tripwiresVisible && tripwireLayer}
      </div>
      {layerToggles && (
        <CanvasRail>
          <LayerControls toggles={layerToggles} />
        </CanvasRail>
      )}
    </div>
  );
};

export default LineCanvas;
